import PageHeader from "app/shared/PageHeader";
import "./Dashboard.scss";
import { useEffect, useState, useRef } from "react";
import { useRecoilState } from "recoil";
import { ExclamationCircleFilled, WarningFilled, ExclamationCircleOutlined, BarChartOutlined } from "@ant-design/icons";
import DescriptionIcon from "@mui/icons-material/Description";
import { Button ,message} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { DASHBOARD_LINK_ZOHO_CRITERIA_FILTER } from "global.constants";
import { getSubdomain } from "app/utils/generic.helper";
import { colorPicker } from "app/utils/color.helper";
import { apiGet } from "app/services/apiServices";


import {
  onboardingMessageShown,
  tabViewStates,
  userInfo,
  workspaceReports,
} from "app/config/States/users";
import Loader from "app/shared/Loader";
import { Modal, Tabs, Table, Tooltip } from "antd";
import Typography from "app/shared/Typography";
import EmptyState from "app/shared/EmptyState";
import ZAViewSelector from "app/shared/ZAViewSelector";
import { Mixpanel } from "app/config/Mixpanel";
import { FinkAnalytics } from "app/utils/GoogleAnalytics";
export default function AppDashboard(props: any) {
 const [activeKey, setActiveKey] = useState("");
 const [reports, setWorkspaceReports] = useRecoilState<any>(workspaceReports);
 const [isLoading, setLoading] = useState(false);
 const [userDetails, setUserDetails] = useRecoilState<any>(userInfo);
  const [onboardingMessage, setShowOnboardingMessage] = useRecoilState<any>(
    onboardingMessageShown,
  );
  const [showModal, setShowModal] = useState(false);
  const [showCriticalModal, setShowCriticalModal] = useState(false);
  const [credentialsData, setCredentialsData] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const previousWorkspaceId = useRef<string | null>(null);

  // Helper functions to manage modal shown/dismissal in localStorage (once per day per user/email)
  // Uses email to ensure each user sees modal independently, even in same workspace
  const getUserIdentifier = (userId: string | number, email?: string): string => {
    // Use email if available (more stable), otherwise fallback to userId
    if (email) {
      return `email_${email.toLowerCase()}`;
    }
    return `user_${String(userId)}`;
  };

  const getModalShownDate = (userId: string | number, email?: string): string | null => {
    if (!userId && !email) return null;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalShown_${identifier}`;
    return localStorage.getItem(key);
  };

  const setModalShownDate = (userId: string | number, email?: string) => {
    if (!userId && !email) return;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalShown_${identifier}`;
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    localStorage.setItem(key, today);
  };

  const getModalDismissalDate = (userId: string | number, email?: string): string | null => {
    if (!userId && !email) return null;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalDismissed_${identifier}`;
    return localStorage.getItem(key);
  };

  const setModalDismissalDate = (userId: string | number, email?: string) => {
    if (!userId && !email) return;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalDismissed_${identifier}`;
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    localStorage.setItem(key, today);
  };

  const wasModalShownToday = (userId: string | number, email?: string): boolean => {
    if (!userId && !email) {
      return false;
    }
    const shownDate = getModalShownDate(userId, email);
    if (!shownDate) {
      return false;
    }
    
    const today = new Date().toISOString().split('T')[0];
    const wasShown = shownDate === today;
    return wasShown;
  };

  const isModalDismissedToday = (userId: string | number, email?: string): boolean => {
    if (!userId && !email) {
      console.log('No userId or email provided for dismissal check');
      return false;
    }
    const dismissalDate = getModalDismissalDate(userId, email);
    if (!dismissalDate) {
      return false;
    }
    
    const today = new Date().toISOString().split('T')[0];
    const isDismissed = dismissalDate === today;
    
    return isDismissed;
  };

 Mixpanel.track("User Landed On Dashboard", { Screen: "Dashboard" });
  const domain: any = getSubdomain();


 const fetchWorkspaceGstinStatus = async () => {
   const response = await apiGet("https://stage-apiplatform.finkraftai.com/api/meta/workspace/gstin/status");
   if (response.status) {
     return response.data;
   }
   return null;
 };


 const fetchUserCreds = async () => {
   try {
     setLoading(true);
     const response = await apiGet("https://stage-apiplatform.finkraftai.com/api/meta/user/credentialsAlert");
    
     if (response.status && response.data) {
       // Store credentials alert data for the table
       // Response structure: { status: true, data: { invalid_gstin: 4, ... } }
   
       setCredentialsData(response.data);
     } else {
       setCredentialsData(null);
     }
   } catch (error) {
     console.error("Error fetching user credentials:", error);
     setCredentialsData(null);
   } finally {
     setTimeout(() => {
       setLoading(false);
     }, 1000);
   }
 };


 useEffect(() => {
   if (reports?.dashboards && reports?.dashboards.length > 0) {
     setActiveKey(reports.dashboards[0].embedurl);
   }
   // else {
   //   setActiveKey("Credential");
   // }


   const hostname = window.location.hostname; 
   const isLocalhost =
     hostname === "localhost" || hostname === "127.0.0.1" || hostname === "";


   if (!isLocalhost && userDetails?.email) {
     const email = userDetails.email.toLowerCase();
     const blockDomains = ["kgrp.in", "finkraft.ai"];
     const userDomain = email.split("@")[1];


     if (!blockDomains.includes(userDomain)) {
       const subdomain = hostname.split(".")[0];


       posthog.identify(userDetails.id, {
         name: userDetails.name,
         email: userDetails.email,
         workspace_name: userDetails.currentWorkspace?.name,
         subdomain: subdomain,
       });


       FinkAnalytics("navbar_option_clicked", {
         option_name: "Dashboard",
         workspace_name: userDetails.currentWorkspace?.name,
         subdomain: subdomain,
       });


       posthog.capture("user_details_captured", {
         name: userDetails.name,
         email: userDetails.email,
         workspace_name: userDetails.currentWorkspace?.name,
         subdomain: subdomain,
         access_date: new Date().toISOString().split("T")[0],
       });
     } else {
       posthog.reset();
     }
   }


   if (!onboardingMessage) setShowModal(true);


   // Call API to fetch user credentials alert
   fetchUserCreds();


   setLoading(true);
   setTimeout(() => {
     setLoading(false);
   }, 2000);
 }, [reports, userDetails]);


 // Show critical modal when credential issues are detected (once per day per email)
 useEffect(() => {
   if (credentialsData && userDetails?.id && userDetails?.email) {
     const userId = userDetails.id;
     const userEmail = userDetails.email;
     
     // Check if modal was already shown or dismissed today for this email
     const wasShownToday = wasModalShownToday(userId, userEmail);
     const isDismissedToday = isModalDismissedToday(userId, userEmail);
     
     if (!wasShownToday && !isDismissedToday) {
       const totalIssues =
         (credentialsData.invalid_gstin || 0) +
         (credentialsData.unknown_gstin || 0) +
         (credentialsData.invalid_airline || 0) +
         (credentialsData.not_available_airline || 0) +
         (credentialsData.invalid_email || 0) +
         (credentialsData.notAvailableEmail || 0);
      
       if (totalIssues > 0) {
     
         // Mark as shown IMMEDIATELY when we decide to show it
         // Uses email as primary identifier (each email sees modal independently)
         setModalShownDate(userId, userEmail);
         setShowCriticalModal(true);
       } else {
         setShowCriticalModal(false);
       }
     } else {
       setShowCriticalModal(false);
     }
   }
 }, [credentialsData, userDetails?.id, userDetails?.email]);

 // Reset modal state if it was dismissed today (safeguard)
 // Note: We don't check for "shown" here because we want to show it once per day
 useEffect(() => {
   if ((userDetails?.id || userDetails?.email) && showCriticalModal) {
     const userId = userDetails.id;
     const userEmail = userDetails.email;
     const isDismissedToday = isModalDismissedToday(userId, userEmail);
     if (isDismissedToday) {
       setShowCriticalModal(false);
     }
   }
 }, [userDetails?.id, userDetails?.email, showCriticalModal]);

 const handleRedirectOnboarding = () => {
   setShowOnboardingMessage(true);
   navigate("/integrations");
 };


 const handleRemindLater = () => {
   console.log('handleRemindLater called');
   setShowCriticalModal(false);
   
   // Save dismissal date for this email (once per day per email)
   const userId = userDetails?.id;
   const userEmail = userDetails?.email;
   if (userId || userEmail) {
     setModalDismissalDate(userId, userEmail);
   }

   // PostHog tracking for "Remind me later" button
   const hostname = window.location.hostname;
   const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "";
  
   console.log('PostHog tracking check:', {
     hostname,
     isLocalhost,
     hasEmail: !!userDetails?.email,
     credentialsData: !!credentialsData
   });
  
   // Calculate total issues
   const totalIssues = credentialsData ?
     (credentialsData.invalid_gstin || 0) +
     (credentialsData.unknown_gstin || 0) +
     (credentialsData.invalid_airline || 0) +
     (credentialsData.not_available_airline || 0) +
     (credentialsData.invalid_email || 0) +
     (credentialsData.notAvailableEmail || 0) : 0;
  
   if (!isLocalhost && userDetails?.email) {
     const email = userDetails.email.toLowerCase();
     const blockDomains = ["kgrp.in", "finkraft.ai"];
     const userDomain = email.split("@")[1];
    

    
     if (!blockDomains.includes(userDomain)) {
       const subdomain = hostname.split(".")[0];
       const workspaceId = userDetails?.currentWorkspace?.id;
       
       const eventData = {
         workspace_id: workspaceId,
         workspace_name: userDetails.currentWorkspace?.name,
         user_id: userDetails.id,
         user_email: userDetails.email,
         subdomain: subdomain,
         total_issues: totalIssues,
         invalid_gstin: credentialsData?.invalid_gstin || 0,
         unknown_gstin: credentialsData?.unknown_gstin || 0,
         invalid_airline: credentialsData?.invalid_airline || 0,
         not_available_airline: credentialsData?.not_available_airline || 0,
         invalid_email: credentialsData?.invalid_email || 0,
         not_available_email: credentialsData?.notAvailableEmail || 0,
         timestamp: new Date().toISOString(),
       };
      
       posthog.capture("critical_credentials_modal_remind_later_clicked", eventData);
     } else {
       console.log('PostHog tracking skipped: blocked domain');
     }
   } else {
     console.log('PostHog tracking skipped:', {
       reason: isLocalhost ? 'localhost' : 'no email'
     });
   }
 };


 const handleGoToCredentials = () => {
   setShowCriticalModal(false);
   
   // Save dismissal date for this email (once per day per email)
   const userId = userDetails?.id;
   const userEmail = userDetails?.email;
   if (userId || userEmail) {
     setModalDismissalDate(userId, userEmail);
   }
   
   navigate("/integrations");
 };


 // Map API response data to table data
 const credentialIssuesData = credentialsData ? [
   {
     key: "1",
     credential: "GST",
     total: (credentialsData.invalid_gstin || 0) + (credentialsData.unknown_gstin || 0),
     wrongCredentials: credentialsData.invalid_gstin || 0,
     notAvailable: credentialsData.unknown_gstin || 0,
   },
   {
     key: "2",
     credential: "Airline",
     total: (credentialsData.invalid_airline || 0) + (credentialsData.not_available_airline || 0),
     wrongCredentials: credentialsData.invalid_airline || 0,
     notAvailable: credentialsData.not_available_airline || 0,
   },
   {
     key: "3",
     credential: "SSR Email",
     total: (credentialsData.invalid_email || 0) + (credentialsData.notAvailableEmail || 0),
     wrongCredentials: credentialsData.invalid_email || 0,
     notAvailable: credentialsData.notAvailableEmail || 0,
   },
 ] : [];


 // Calculate total issues count (sum of all "total" values from the table)
 const totalIssuesCount = credentialIssuesData.reduce((sum, item) => sum + (item.total || 0), 0);
  // Debug: Log the calculated total issues count
 


  // Helper functions for table styling
 const getHeaderCellStyle = () => ({
   style: {
     backgroundColor: "transparent",
     background: "transparent",
     color: "#BDBDBD",
     fontSize: "12px",
   },
 });


 const getCellStyle = (index?: number) => ({
   style: {
     borderBottom: index === 0 || index === 1 ? "1px solid #f0f0f0" : "none",
   },
 });


 const getHeaderTitle = (text: string) => (
   <span style={{ color: "#BDBDBD", fontSize: "12px" }}>{text}</span>
 );


 const credentialIssuesColumns = [
   {
     title: getHeaderTitle("Credentials"),
     dataIndex: "credential",
     key: "credential",
     onHeaderCell: getHeaderCellStyle,
     onCell: (_record: any, index?: number) => getCellStyle(index),
   },
   {
     title: getHeaderTitle("No."),
     dataIndex: "total",
     key: "total",
     onHeaderCell: getHeaderCellStyle,
     onCell: (_record: any, index?: number) => getCellStyle(index),
   },
   // {
   //   title: getHeaderTitle("Invalid"),
   //   dataIndex: "invalid",
   //   key: "invalid",
   //   onHeaderCell: getHeaderCellStyle,
   //   onCell: (_record: any, index?: number) => getCellStyle(index),
   // },
   {
     title: getHeaderTitle("Wrong Credentials"),
     dataIndex: "wrongCredentials",
     key: "wrongCredentials",
     onHeaderCell: getHeaderCellStyle,
     onCell: (_record: any, index?: number) => getCellStyle(index),
   },
   {
     title: getHeaderTitle("Not Available"),
     dataIndex: "notAvailable",
     key: "notAvailable",
     onHeaderCell: getHeaderCellStyle,
     onCell: (_record: any, index?: number) => getCellStyle(index),
   },
   {
     title: "",
     key: "action",
     render: (_text: any, record: any) => {
       // Tooltip messages based on credential type
       const getTooltipMessage = () => {
         switch (record.credential) {
           case "GST":
             return "Core ITC reconciliation is blocked, risking compliance and tax credit loss";
           case "Airline":
             return "6.6 Crore K3 Value Blocked, preventing recovery on airline expenses";
           case "SSR Email":
             return "14.2 Crore K3 Value Blocked, email-dependent invoices are inaccessible.";
           default:
             return "";
         }
       };


       return (
        //  <Tooltip title={getTooltipMessage()}>
        <Tooltip >
           <ExclamationCircleOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
         </Tooltip>
       );
     },
     onHeaderCell: () => ({
       style: {
         backgroundColor: "transparent",
         background: "transparent",
       },
     }),
     onCell: (_record: any, index?: number) => getCellStyle(index),
   },
 ];


 const getCredentialDashboardLink = () => {
   let link =
     "https://analytics.zoho.in/open-view/103074000038657277/3b131ffff66b66a9688aaeeddc4fa06d";
   if (userDetails.currentWorkspace?.name.includes("Admin")) {
     return link;
   } else {
     const workspaceName = userDetails.currentWorkspace?.name;
     const encodedWorkspaceName = encodeURIComponent(workspaceName);
     const filterKey =
       DASHBOARD_LINK_ZOHO_CRITERIA_FILTER[domain]?.[activeKey] || "workspace";
     const actualUrl = `${link}?ZOHO_CRITERIA=%22${filterKey}%22%3D%27${encodedWorkspaceName}%27`;
     return actualUrl;
   }
 };
 return isLoading ? (
   <Loader />
 ) : (
   <div className="Dashboard">
     <div className="ScreenContainer">
       <PageHeader
         title={"Dashboard"}
         leftActions={
           <div style={{ display: "flex", alignItems: "center" }}>
             <ZAViewSelector
               activeKey={activeKey === "Credential" ? "" : activeKey}
               items={reports?.dashboards || []}
               onViewChange={(data: any, fullObj: any) => setActiveKey(data)}
             />
             <div style={{ marginLeft: 12 }}>
               <Tabs
                 tabBarStyle={{
                   borderBottom: "none",
                   margin: 0,
                 }}
                 activeKey={activeKey}
                 onChange={(tabItem: string) => setActiveKey(tabItem)}
               />
             </div>
           </div>
         }
         rightActions={
           (userDetails?.email?.includes("finkraft.ai") ||
             userDetails?.email?.includes("kgrp.in")) ? (
             <Button
               size="small"
               icon={<DescriptionIcon style={{ fontSize: 14, color: colorPicker("primary.700") }} />}
               style={{ borderColor: colorPicker("primary.700"), color: colorPicker("primary.700") }}
               onClick={() => navigate("/reports")}
             >
               Reports
             </Button>
           ) : null
         }
       />


       <div className="TableContainer">
         {activeKey === "Credential" ? (
           <div style={{ height: "100%" }}>
             <iframe
               height="100%"
               style={{ width: "100%" }}
               scrolling="no"
               title="Iframe"
               src={getCredentialDashboardLink()}
             ></iframe>
           </div>
         ) : !reports?.dashboards || reports?.dashboards?.length === 0 ? (
           <EmptyState
             title="No Dashboards Available"
             description="Try changing workspace for dashboard view"
           />
         ) : (
           <div style={{ height: "100%" }}>
             <iframe
               height="100%"
               style={{ width: "100%" }}
               scrolling="no"
               title="Iframe"
               src={activeKey}
             ></iframe>
           </div>
         )}
       </div>
     </div>
     <Modal
       title={
         <Typography variant="h6" style={{ fontWeight: 600, color: "#181D1F", fontSize: "20px", lineHeight: '120%' }}>
           Critical Account Status
         </Typography>
       }
       open={showCriticalModal}
       onCancel={() => {
         setShowCriticalModal(false);
         
         // Save dismissal date for this email (once per day per email)
         const userId = userDetails?.id;
         const userEmail = userDetails?.email;
         if (userId || userEmail) {
           setModalDismissalDate(userId, userEmail);
         }
       }}
       closable={true}
       width={700}
       centered
       footer={null}
       className="critical-account-modal"
     >
       <div style={{ padding: "8px 0" }}>
         {/* Warning Section */}
         <div
           style={{
             border: "2px #ff4d4f",
             borderRadius: "4px",
             padding: '8px 16px',
             marginBottom: "24px",
             height: '80px',
             background: 'rgba(231, 0, 11, 0.08)',
             display: 'flex',
             flexDirection: 'column',
             justifyContent: 'center',
             gap: '4px',


           }}
         >
           <div
             style={{
               display: "flex",
               alignItems: "center",
               gap: '8px',
             }}
           >
             <WarningFilled
               style={{
                 color: "#EC221F", fontSize: "20px", width: '20.527px',
                 height: '18px'
               }}
             />
             <Typography
               variant="body1"
               style={{
                 fontWeight: 600, color: ' #EC221F', fontSize: '14px',
                 lineHeight: '24px', fontFamily:"Noto Sans"
               }}
             >
               Issues found ({totalIssuesCount})
             </Typography>
           </div>




           <Typography
             variant="body2"
             style={{
               color: "#EC221F", fontSize: '12px', lineHeight: '16px', fontWeight: 500,
               letterspacing: '-0.024px',marginLeft:"27px",fontFamily:"Noto Sans"
             }}
           >
             Failing to update these credentials immediately will prevent core
             functionality and compromise compliance. Please click 'Go to Credentials and Fix now' to resolve.
           </Typography>
         </div>






         {/* Breakdown Section */}
         <div style={{ marginBottom: "24px" ,color: '#181D1F',fontSize: '16px', lineHeight: '24px', fontWeight: 600 }}>
           <Typography
             variant="body1"
             style={{ fontWeight: 600, marginBottom: "16px", fontFamily:"Noto Sans" }}
           >
             Breakdown of Issues ({totalIssuesCount})
           </Typography>
           <div className="credential-issues-table-wrapper">
             <Table
               dataSource={credentialIssuesData}
               columns={credentialIssuesColumns}
               pagination={false}
               size="small"
               bordered={false}
               style={{ marginBottom: "16px" , fontFamily:"Noto Sans"}}
             />
           </div>
         </div>


         {/* Action Buttons */}
         <div
           style={{
             display: "flex",
             justifyContent: "flex-end",
             gap: "12px",
             marginTop: "24px",
             padding: '0 16px',
           }}
         >
           <Button
             onClick={handleRemindLater}
             style={{
               border: "1px solid #03A5B7",
               color: "#03A5B7",
               fontSize: '14px',
                fontFamily:"Noto Sans"
             }}
           >
             Remind me later
           </Button>
           <Button
             type="primary"
             onClick={handleGoToCredentials}
             style={{
               background: '#03A5B7',
               padding: '0 16px',
                fontFamily:"Noto Sans"
             }}
           >
             Go to Credentials and Fix now
           </Button>
         </div>
       </div>
     </Modal>


   </div>
 );
}









